const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
const MANAGED_NAV_CLASS = 'nav--desktop-managed';
const ITEM_OPEN_CLASS = 'nav__item--desktop-open';
const BLUR_ROOT_CLASS = 'nav__desktop-blur-root';
const BLUR_PANEL_CLASS = 'nav__desktop-blur-panel';
const CLOSE_DELAY_MS = 90;

let destroyDesktopNav = null;

export const initDesktopNav = () => {
  destroyDesktopNav?.();

  const nav = document.querySelector('.header-site__navbar.nav');

  if (!(nav instanceof HTMLElement)) {
    return;
  }

  const items = Array.from(nav.querySelectorAll('.nav__item--has-children')).filter(
    (item) => item instanceof HTMLElement
  );

  if (!items.length) {
    return;
  }

  const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  const closeTimers = new Map();
  const blurPanels = new Map();
  let isBound = false;
  let blurRoot = null;

  const ensureBlurRoot = () => {
    if (blurRoot?.isConnected) {
      return blurRoot;
    }

    const root = document.createElement('div');
    root.className = `header-site__desktop-blur-root ${BLUR_ROOT_CLASS}`;
    document.body.append(root);
    blurRoot = root;
    return blurRoot;
  };

  const clearCloseTimer = (item) => {
    const timerId = closeTimers.get(item);

    if (timerId) {
      window.clearTimeout(timerId);
      closeTimers.delete(item);
    }
  };

  const removeBlurPanel = (item) => {
    const panel = blurPanels.get(item);

    if (!panel) {
      return;
    }

    panel.remove();
    blurPanels.delete(item);
  };

  const syncBlurPanel = (item) => {
    const dropdown = item.querySelector(':scope > .nav__dropdown');

    if (!(dropdown instanceof HTMLElement)) {
      removeBlurPanel(item);
      return;
    }

    const root = ensureBlurRoot();
    const rect = dropdown.getBoundingClientRect();

    if (!rect.width || !rect.height) {
      removeBlurPanel(item);
      return;
    }

    let panel = blurPanels.get(item);

    if (!panel) {
      panel = document.createElement('div');
      panel.className = `header-site__desktop-blur-panel ${BLUR_PANEL_CLASS}`;
      root.append(panel);
      blurPanels.set(item, panel);
    }

    panel.style.top = `${rect.top}px`;
    panel.style.left = `${rect.left}px`;
    panel.style.width = `${rect.width}px`;
    panel.style.height = `${rect.height}px`;
  };

  const syncOpenBlurPanels = () => {
    blurPanels.forEach((panel, item) => {
      if (!(item instanceof HTMLElement) || !item.classList.contains(ITEM_OPEN_CLASS)) {
        removeBlurPanel(item);
        return;
      }

      syncBlurPanel(item);
    });
  };

  const closeBranch = (item) => {
    clearCloseTimer(item);
    item.classList.remove(ITEM_OPEN_CLASS);
    removeBlurPanel(item);

    item.querySelectorAll(`.${ITEM_OPEN_CLASS}`).forEach((nestedItem) => {
      if (!(nestedItem instanceof HTMLElement)) {
        return;
      }

      clearCloseTimer(nestedItem);
      nestedItem.classList.remove(ITEM_OPEN_CLASS);
      removeBlurPanel(nestedItem);
    });
  };

  const closeSiblingBranches = (item) => {
    const parentList = item.parentElement;

    if (!parentList) {
      return;
    }

    Array.from(parentList.children).forEach((sibling) => {
      if (!(sibling instanceof HTMLElement) || sibling === item) {
        return;
      }

      if (!sibling.classList.contains('nav__item--has-children')) {
        return;
      }

      closeBranch(sibling);
    });
  };

  const clearAncestorCloseTimers = (item) => {
    let ancestor = item.parentElement?.closest('.nav__item--has-children');

    while (ancestor instanceof HTMLElement) {
      clearCloseTimer(ancestor);
      ancestor = ancestor.parentElement?.closest('.nav__item--has-children');
    }
  };

  const syncAncestorBranch = (item) => {
    let ancestor = item.parentElement?.closest('.nav__item--has-children');

    while (ancestor instanceof HTMLElement) {
      clearCloseTimer(ancestor);
      ancestor.classList.add(ITEM_OPEN_CLASS);
      syncBlurPanel(ancestor);
      ancestor = ancestor.parentElement?.closest('.nav__item--has-children');
    }
  };

  const openItem = (item) => {
    if (!desktopMediaQuery.matches) {
      return;
    }

    const dropdown = item.querySelector(':scope > .nav__dropdown');
    const isLevel3Dropdown =
      dropdown instanceof HTMLElement && dropdown.classList.contains('nav__dropdown--level-3');

    clearCloseTimer(item);
    clearAncestorCloseTimers(item);
    closeSiblingBranches(item);

    if (isLevel3Dropdown) {
      syncBlurPanel(item);
    }

    item.classList.add(ITEM_OPEN_CLASS);
    syncAncestorBranch(item);
    syncBlurPanel(item);
  };

  const scheduleClose = (item) => {
    clearCloseTimer(item);

    const timerId = window.setTimeout(() => {
      closeTimers.delete(item);
      closeBranch(item);
    }, CLOSE_DELAY_MS);

    closeTimers.set(item, timerId);
  };

  const handleMouseEnter = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) {
      return;
    }

    openItem(item);
  };

  const handleMouseLeave = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) {
      return;
    }

    scheduleClose(item);
  };

  const handleResize = () => {
    if (!desktopMediaQuery.matches || !isBound) {
      return;
    }

    syncOpenBlurPanels();
  };

  const bindDesktopNav = () => {
    if (isBound) {
      return;
    }

    isBound = true;
    nav.classList.add(MANAGED_NAV_CLASS);
    ensureBlurRoot();

    items.forEach((item) => {
      item.addEventListener('mouseenter', handleMouseEnter);
      item.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  const unbindDesktopNav = () => {
    if (!isBound) {
      return;
    }

    isBound = false;
    nav.classList.remove(MANAGED_NAV_CLASS);

    items.forEach((item) => {
      item.removeEventListener('mouseenter', handleMouseEnter);
      item.removeEventListener('mouseleave', handleMouseLeave);
      closeBranch(item);
    });
    blurPanels.forEach((panel) => {
      panel.remove();
    });
    blurPanels.clear();

    blurRoot?.remove();
    blurRoot = null;
  };

  const handleBreakpointChange = (event) => {
    if (event.matches) {
      bindDesktopNav();
      return;
    }

    unbindDesktopNav();
  };

  if (desktopMediaQuery.matches) {
    bindDesktopNav();
  }

  window.addEventListener('resize', handleResize);

  if (typeof desktopMediaQuery.addEventListener === 'function') {
    desktopMediaQuery.addEventListener('change', handleBreakpointChange);
  } else if (typeof desktopMediaQuery.addListener === 'function') {
    desktopMediaQuery.addListener(handleBreakpointChange);
  }

  destroyDesktopNav = () => {
    unbindDesktopNav();
    window.removeEventListener('resize', handleResize);

    if (typeof desktopMediaQuery.removeEventListener === 'function') {
      desktopMediaQuery.removeEventListener('change', handleBreakpointChange);
    } else if (typeof desktopMediaQuery.removeListener === 'function') {
      desktopMediaQuery.removeListener(handleBreakpointChange);
    }
  };
};
